import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from "react-redux";

import { Questionnaire1 } from './Questionnaire1'
import { Questionnaire2 } from './Questionnaire2';
import { Questionnaire3 } from './Questionnaire3';
import { Questionnaire4 } from './Questionnaire4';
import { Questionnaire5 } from './Questionnaire5';
import { Questionnaire6 } from './Questionnaire6';
import { Questionnaire7 } from './Questionnaire7';
import { DynamicQuestionnaire } from './Dynamic/DynamicQuestionnaire';

export const QuestionnaireLayout = ({lang,setLang}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const orderData = useSelector((state) => state.questionAnswer.orderData);
  const orderId = useSelector((state) => state.questionAnswer.orderId);

 useEffect(()=>{
  document.documentElement.scrollTo({
    top: 0,
    left: 0
  })
 },[])

  const {pageno} = useParams();
  const [formData, setFormData] = useState();
  
  // Check if order has brand_identity
  const hasBrandIdentity = orderData?.brand_identity && Object.keys(orderData.brand_identity).length > 0;
  // Check if order has illustrations
  const hasIllustrations = orderData?.illustrations === "illustrations";

  // Determine which pages should be shown
  const shouldShowPage = (pageNum) => {
      // return true;
    // Pages 1, 2, and 7 are always shown
    if (pageNum === 1 || pageNum === 2 || pageNum === 7) {
      return true;
    }
    
    // Pages 3, 4, 5 only show if hasBrandIdentity is true
    if (pageNum === 3 || pageNum === 4 || pageNum === 5) {
      return hasBrandIdentity;
    }
    
    // Page 6 only shows if hasIllustrations is true
    if (pageNum === 6) {
      return hasIllustrations;
    }
    
    return false;
  };

  // Get the previous valid page number based on current page
  const getPreviousPage = (currentPage) => {
    if (currentPage === 1) {
      return null; // No previous page
    }
    
    if (currentPage === 2) {
      return 1; // Always go to page 1
    }
    
    if (currentPage === 3) {
      return 2; // Always go to page 2
    }
    
    if (currentPage === 4) {
      return 3; // Always go to page 3 (only shown if hasBrandIdentity)
    }
    
    if (currentPage === 5) {
      return 4; // Always go to page 4 (only shown if hasBrandIdentity)
    }
    
    if (currentPage === 6) {
      // If hasBrandIdentity, go to page 5, else go to page 2
      return hasBrandIdentity ? 5 : 2;
    }
    
    if (currentPage === 7) {
      // If hasIllustrations, go to page 6
      // Else if hasBrandIdentity, go to page 5
      // Else go to page 2
      if (hasIllustrations) {
        return 6;
      } else if (hasBrandIdentity) {
        return 5;
      } else {
        return 2;
      }
    }
    
    return null;
  };

  // Redirect if trying to access a page that shouldn't be shown
  useEffect(() => {
    const pageNum = parseInt(pageno);
    if (!shouldShowPage(pageNum)) {
      // Redirect to appropriate page
      if (pageNum === 3 || pageNum === 4 || pageNum === 5) {
        if (!hasBrandIdentity) {
          // If no brand_identity, skip to page 6 if hasIllustrations, else skip to page 7
          if (hasIllustrations) {
            navigate('/questionnaire/6', { replace: true });
          } else {
            navigate('/questionnaire/7', { replace: true });
          }
        }
      } else if (pageNum === 6) {
        if (!hasIllustrations) {
          // If no illustrations, skip to page 7
          navigate('/questionnaire/7', { replace: true });
        }
      }
    }
  }, [pageno, hasBrandIdentity, hasIllustrations, navigate]);

  // Centralized back navigation handler
  const handleBackNavigation = (currentPage) => {
    const previousPage = getPreviousPage(currentPage);
    if (previousPage) {
      navigate(`/questionnaire/${previousPage}`);
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const questionnairePages = {
    1:<Questionnaire1 setFormData={setFormData} formData={formData} changeLang={lang} setChangeLang={setLang} getPreviousPage={getPreviousPage} handleBackNavigation={handleBackNavigation}/>,
    2:<Questionnaire2 setFormData={setFormData} formData={formData} changeLang={lang} setChangeLang={setLang} getPreviousPage={getPreviousPage} handleBackNavigation={handleBackNavigation}/>,
    3:<Questionnaire3 setFormData={setFormData} formData={formData} changeLang={lang} setChangeLang={setLang} getPreviousPage={getPreviousPage} handleBackNavigation={handleBackNavigation}/>,
    4:<Questionnaire4 setFormData={setFormData} formData={formData} changeLang={lang} setChangeLang={setLang} getPreviousPage={getPreviousPage} handleBackNavigation={handleBackNavigation}/>,
    5:<Questionnaire5 setFormData={setFormData} formData={formData} changeLang={lang} setChangeLang={setLang} getPreviousPage={getPreviousPage} handleBackNavigation={handleBackNavigation}/>,
    6:<Questionnaire6 setFormData={setFormData} formData={formData} changeLang={lang} setChangeLang={setLang} getPreviousPage={getPreviousPage} handleBackNavigation={handleBackNavigation}/>,
    7:<Questionnaire7 setFormData={setFormData} formData={formData} changeLang={lang} setChangeLang={setLang} getPreviousPage={getPreviousPage} handleBackNavigation={handleBackNavigation}/>
  }
    
  // Routing decision. orderData is hydrated by Approutes.js's getQuestionAnswer
  // useEffect, which fires whenever orderId changes. Until that fetch returns
  // we don't yet know which flow this order belongs to, so show a loader
  // rather than briefly flash the legacy UI for what may be a dynamic order.
  // Falls through to legacy when orderData arrives with the flag === false.
  const orderDataLoaded =
    orderData && (orderData.id || orderData.use_dynamic_questionnaire !== undefined);

  if (orderId && !orderDataLoaded) {
    return (
      <div style={{padding: "60px 0", textAlign: "center"}}>Loading…</div>
    );
  }

  if (orderData?.use_dynamic_questionnaire) {
    return (
      <div style={{overflowX: "hidden"}}>
        <DynamicQuestionnaire lang={lang} setLang={setLang} />
      </div>
    );
  }

  return (
    <div style={{overflowX: "hidden"}}>
        {shouldShowPage(parseInt(pageno)) ? questionnairePages[pageno] : null}
    </div>
  )
}
